import { useCallback, useEffect, useState } from 'react';
import { Banner, BlockStack, Button, InlineStack, Text, TextField } from '@shopify/polaris';
import axios from 'axios';
import agent from '../../app/api/agent';
import { LegacyTargetPreparation } from '../../app/models/Admin';
import { formatLocalDateTime } from './utils/dateTime';

type Props = { sourceShopId: number; domain: string; token: string; onUnauthorized: () => void; onCompleted: () => void };
const stages: Record<string, string> = {
  Queued: 'Waiting for ShopifySyncService', VerifyingIdentity: 'Verifying the Shopify installation',
  ImportingRuntime: 'Importing settings and vehicles', CopyingProducts: 'Copying products',
  CopyingVariants: 'Copying variants', CopyingCollections: 'Copying collections',
  VerifyingTarget: 'Checking the prepared target', ReadyForPilot: 'Target preparation complete',
};
const failureDetails: Record<string, string> = {
  SourceSettingsMissing: 'PFV1 has no saved settings record for this store. Review its settings in PFV1 before retrying. No default settings were substituted.',
  SourceSettingsAmbiguous: 'PFV1 has multiple settings records for this store. Resolve the duplicate records before retrying.',
  RuntimeImportIncomplete: 'PFV2 is missing the imported legacy settings or storefront settings. Check the PFV1 source settings and the runtime import before retrying.',
};

export default function LegacyTargetPreparationPanel({ sourceShopId, domain, token, onUnauthorized, onCompleted }: Props) {
  const [job, setJob] = useState<LegacyTargetPreparation | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState('');
  const fail = useCallback((e: unknown) => {
    if (axios.isAxiosError(e) && e.response?.status === 401) onUnauthorized();
    else setError(axios.isAxiosError(e) && typeof e.response?.data?.message === 'string'
      ? e.response.data.message : 'Unable to load or queue preparation. Reload its status before retrying.');
  }, [onUnauthorized]);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await agent.Admin.targetPreparation(token, sourceShopId);
      setJob(result.operation); setError(null);
    } catch (e) { fail(e); } finally { setLoading(false); }
  }, [token, sourceShopId, fail]);
  useEffect(() => { void load(); }, [load]);
  const active = job?.status === 'Queued' || job?.status === 'Running';
  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => void load(), 10000);
    return () => window.clearInterval(timer);
  }, [active, load]);
  useEffect(() => { if (job?.status === 'Completed') onCompleted(); }, [job?.id, job?.status, onCompleted]);
  const prepare = async () => {
    if (!domain || confirmation !== domain || active || busy || loading || error) return;
    setBusy(true); setError(null);
    try { setJob(await agent.Admin.prepareLegacyTarget(token, sourceShopId, confirmation)); setConfirmation(''); }
    catch (e) { fail(e); } finally { setBusy(false); }
  };
  return <BlockStack gap="300">
    <InlineStack align="space-between"><Text as="h3" variant="headingMd">Prepare PFV2 target</Text>
      <Button onClick={() => void load()} loading={loading} disabled={busy}>Reload preparation</Button></InlineStack>
    <Text as="p">Creates this store’s PFV2 record and merchant identity, imports its settings and vehicles, and copies its product catalog. PFV1 continues serving. Pilot activation is a separate action below.</Text>
    {error ? <Banner tone="critical">{error}</Banner> : null}
    {job ? <Banner tone={job.status === 'Failed' ? 'critical' : job.status === 'Completed' ? 'success' : 'info'}
      title={job.status === 'Failed' ? 'Preparation failed' : stages[job.stage] || job.stage}>
      <p>{job.productsCopied} products · {job.variantsCopied} variants · {job.collectionsCopied} collections copied.</p>
      {job.targetShopId ? <p>PFV2 target: #{job.targetShopId}</p> : null}
      {job.status === 'Queued' ? <p>Runs in ShopifySyncService and continues after you leave this page.</p> : null}
      {job.status === 'Completed' ? <p>Target preparation is complete. Use the pilot controls above to review or change the serving backend.</p> : null}
      {job.failureCode ? <p>Failure: {job.failureCode}. The target has not been activated. Resolve the cause before retrying.</p> : null}
      {job.failureCode && failureDetails[job.failureCode] ? <p>{failureDetails[job.failureCode]}</p> : null}
      {job.heartbeatAt ? <p>Last worker update: {formatLocalDateTime(job.heartbeatAt)}</p> : null}
    </Banner> : null}
    {job?.status !== 'Completed' ? <>
      <TextField label="Confirm the store to prepare" value={confirmation} onChange={setConfirmation}
        helpText={`Type ${domain} exactly.`} autoComplete="off" disabled={active || busy} />
      <InlineStack><Button variant="primary" onClick={() => void prepare()}
        disabled={!domain || confirmation !== domain || active || busy || loading || !!error} loading={busy}>
        {job?.status === 'Failed' ? 'Retry target preparation' : 'Prepare PFV2 target'}
      </Button></InlineStack>
    </> : null}
  </BlockStack>;
}
